import React, { createContext, useContext, useState, useCallback } from 'react';
import ActionConfirmModal, { ConfirmTypes } from '../components/ActionConfirmModal';

// Create the context
const ConfirmationContext = createContext(null);

// Provider component
export const ConfirmationProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: 'Confirm Action',
    message: '',
    description: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'default',
    icon: null,
    infoText: '',
    details: null,
    loading: false,
    loadingText: 'Processing...',
    onConfirm: null,
    onCancel: null,
  });

  // Show confirmation modal with options
  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      setModalState({
        isOpen: true,
        title: options.title || 'Confirm Action',
        message: options.message || '',
        description: options.description || '',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        type: options.type || 'default',
        icon: options.icon || null,
        infoText: options.infoText || '',
        details: options.details || null,
        loading: false,
        loadingText: options.loadingText || 'Processing...',
        onConfirm: async () => {
          // Set loading state
          setModalState(prev => ({ ...prev, loading: true }));
          
          // If there's an async action, wait for it
          if (options.onConfirm) {
            try {
              await options.onConfirm();
            } catch (error) {
              console.error('Confirmation action error:', error);
            }
          }
          
          // Close modal and resolve
          setModalState(prev => ({ ...prev, isOpen: false, loading: false }));
          resolve(true);
        },
        onCancel: () => {
          setModalState(prev => ({ ...prev, isOpen: false }));
          if (options.onCancel) options.onCancel();
          resolve(false);
        },
      });
    });
  }, []);

  // Quick confirm methods using pre-defined types
  const confirmAction = useCallback((actionType, options = {}) => {
    const preset = ConfirmTypes[actionType] || {};
    return confirm({
      ...preset,
      ...options,
      title: options.title || preset.title,
      type: options.type || preset.type,
      icon: options.icon || preset.icon,
      confirmText: options.confirmText || preset.confirmText,
      infoText: options.infoText || preset.infoText,
    });
  }, [confirm]);

  // Close modal
  const closeModal = useCallback(() => {
    setModalState(prev => ({ ...prev, isOpen: false, loading: false }));
  }, []);

  // Handle confirm button click
  const handleConfirm = () => {
    if (modalState.onConfirm) {
      modalState.onConfirm();
    }
  };

  // Handle cancel/close
  const handleClose = () => {
    if (modalState.onCancel) {
      modalState.onCancel();
    } else {
      closeModal();
    }
  };

  return (
    <ConfirmationContext.Provider value={{ confirm, confirmAction, closeModal }}>
      {children}
      <ActionConfirmModal
        isOpen={modalState.isOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title={modalState.title}
        message={modalState.message}
        description={modalState.description}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
        type={modalState.type}
        icon={modalState.icon}
        loading={modalState.loading}
        loadingText={modalState.loadingText}
        infoText={modalState.infoText}
        details={modalState.details}
      />
    </ConfirmationContext.Provider>
  );
};

// Custom hook to use the confirmation context
export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
};

export default ConfirmationContext;
